import { type ItemType, itemTypes, type PriorityRule, type RulesPatch, tagSchema } from '@hugo/core'
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { moveRule, ruleLabel, TYPE_LABELS } from '@/lib/rules'

type Kind = PriorityRule['when']['kind']

const KINDS: { kind: Kind; label: string }[] = [
  { kind: 'tag', label: 'Tag' },
  { kind: 'person', label: 'Person' },
  { kind: 'type', label: 'Type of work' },
  { kind: 'due_within', label: 'Due within' },
]

const DUE_CHOICES = [0, 1, 2, 3, 5, 7, 14]

/**
 * Settings → Rules → Priority: rules that set the priority of items the user has not set
 * themselves, in order (the first that matches wins). Each change saves at once.
 */
export function PriorityRulesSection({
  rules,
  disabled,
  save,
}: {
  rules: readonly PriorityRule[]
  disabled: boolean
  save: (patch: RulesPatch, done: string) => Promise<void>
}) {
  const [kind, setKind] = useState<Kind>('tag')
  const [text, setText] = useState('')
  const [type, setType] = useState<ItemType>('admin')
  const [days, setDays] = useState(2)
  const [priority, setPriority] = useState<PriorityRule['priority']>('high')
  const [error, setError] = useState<string | null>(null)

  const draft = (): PriorityRule | null => {
    if (kind === 'tag') {
      const tag = tagSchema.safeParse(text)
      return tag.success ? { when: { kind, tag: tag.data }, priority } : null
    }
    if (kind === 'person') {
      const name = text.trim()
      return name ? { when: { kind, name }, priority } : null
    }
    if (kind === 'type') return { when: { kind, type }, priority }
    return { when: { kind, days }, priority }
  }

  const add = async () => {
    const rule = draft()
    if (!rule) {
      setError(kind === 'tag' ? 'A tag is one word, e.g. client' : 'Name a person')
      return
    }
    setError(null)
    await save({ priorityRules: [...rules, rule] }, `Rule added: ${ruleLabel(rule)}`)
    setText('')
  }

  return (
    <section className="flex flex-col gap-2" aria-labelledby="rules-priority">
      <h3 id="rules-priority" className="text-sm font-medium">
        Priority
      </h3>
      <p className="text-sm text-muted-foreground">
        For items you haven’t given a priority yourself. The first rule that matches decides.
      </p>
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="no-priority-rules">
          No rules yet: everything is normal unless you say otherwise.
        </p>
      ) : (
        <ol className="divide-y rounded-lg border">
          {rules.map((rule, index) => {
            const label = ruleLabel(rule)
            return (
              <li
                // Rules have no ids; their place is their identity in this list.
                // biome-ignore lint/suspicious/noArrayIndexKey: the order is the rule set itself.
                key={`${index}:${label}`}
                className="flex items-center justify-between gap-2 px-3 py-1.5"
                data-testid="priority-rule"
              >
                <span className="min-w-0 truncate text-sm">{label}</span>
                <span className="flex shrink-0 items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move “${label}” up`}
                    disabled={disabled || index === 0}
                    onClick={() =>
                      void save({ priorityRules: moveRule(rules, index, -1) }, 'Rule moved up')
                    }
                  >
                    <ArrowUpIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move “${label}” down`}
                    disabled={disabled || index === rules.length - 1}
                    onClick={() =>
                      void save({ priorityRules: moveRule(rules, index, 1) }, 'Rule moved down')
                    }
                  >
                    <ArrowDownIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove “${label}”`}
                    disabled={disabled}
                    onClick={() =>
                      void save(
                        { priorityRules: rules.filter((_, i) => i !== index) },
                        `Rule removed: ${label}`,
                      )
                    }
                  >
                    <XIcon aria-hidden="true" />
                  </Button>
                </span>
              </li>
            )
          })}
        </ol>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        aria-label="Add a priority rule"
        onSubmit={(e) => {
          e.preventDefault()
          void add()
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="priority-rule-kind" className="text-xs font-normal text-muted-foreground">
            When
          </Label>
          <NativeSelect
            id="priority-rule-kind"
            size="sm"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as Kind)
              setError(null)
            }}
          >
            {KINDS.map((k) => (
              <NativeSelectOption key={k.kind} value={k.kind}>
                {k.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <div className="flex min-w-32 flex-1 flex-col gap-1">
          <Label
            htmlFor="priority-rule-value"
            className="text-xs font-normal text-muted-foreground"
          >
            {kind === 'tag'
              ? 'Which tag'
              : kind === 'person'
                ? 'Who'
                : kind === 'type'
                  ? 'Which type'
                  : 'Within'}
          </Label>
          {kind === 'type' ? (
            <NativeSelect
              id="priority-rule-value"
              size="sm"
              value={type}
              onChange={(e) => setType(e.target.value as ItemType)}
            >
              {itemTypes.map((t) => (
                <NativeSelectOption key={t} value={t}>
                  {TYPE_LABELS[t]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : kind === 'due_within' ? (
            <NativeSelect
              id="priority-rule-value"
              size="sm"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {DUE_CHOICES.map((d) => (
                <NativeSelectOption key={d} value={d}>
                  {d === 0 ? 'Today' : d === 1 ? '1 day' : `${d} days`}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : (
            <Input
              id="priority-rule-value"
              className="h-8"
              placeholder={kind === 'tag' ? 'client' : 'Anna Keller'}
              value={text}
              aria-invalid={error !== null}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Label
            htmlFor="priority-rule-priority"
            className="text-xs font-normal text-muted-foreground"
          >
            Priority
          </Label>
          <NativeSelect
            id="priority-rule-priority"
            size="sm"
            value={priority}
            onChange={(e) => setPriority(e.target.value as PriorityRule['priority'])}
          >
            <NativeSelectOption value="high">High</NativeSelectOption>
            <NativeSelectOption value="low">Low</NativeSelectOption>
          </NativeSelect>
        </div>
        <Button type="submit" size="sm" disabled={disabled}>
          <PlusIcon aria-hidden="true" />
          Add rule
        </Button>
      </form>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  )
}
